import { Router, ScreenBase } from "@frui.ts/screens";
import { action, computed, observable, reaction } from "mobx";
import LocalizationService from "services/localizationService";
import ContactViewModel from "./contactViewModel";
import AppRouter from "services/appRouter";
import PaymentViewModel from "../components/paymentViewModel";
import TermsAgreementViewModel from "../components/termAgreementViewModel";
import OrderRecapitulationViewModel from "../components/orderRecapitulationViewModel";
import NotificationPreferencesRecapViewModel from "../components/notificationPreferencesRecapViewModel";
import GdprInfoViewModel from "../components/gdprInfoViewModel";
import { interfaces } from "inversify";
import ChargeOrder, { ChargeOrderStatus, ChargeOrderValidationRules } from "models/chargeOrder";
import { attachAutomaticBlurValidator } from "utils/validationPatch";
import type { ErrorMessage } from "viewModels/common/types";
import { scrollToElement } from "utils/scrollTo";
import { validate } from "@frui.ts/validation";
import ChargeOrderService from "services/chargeOrderService";
import GooglePayMetadata from "models/googlePayMetadata";
import React from "react";
import { PaymentType } from "models/payment";
import * as Sentry from "@sentry/react";
import EventBus, {
  getGooglePayMetadataFailure,
  getGooglePayMetadataSuccess,
  googlePayPaymentBrowserFingerprintLoaded,
  paymentCompleteAlert,
  paymentEmailAddressInvalid,
  submitGooglePayPaymentBrowserFingerprint,
  submitGooglePayPaymentFailure,
} from "../../../../services/eventBus";
import PaymentService from "services/payment/paymentService";
import { isAfter } from "date-fns";
import { startOfCzechDay } from "utils/dateUtils";
import ChargeOrderRepository from "repositories/chargeOrderRepository";
import TurnstileViewModel from "viewModels/common/turnstileViewModel";
import ChargeOrderStorageRepository from "repositories/chargeOrderStorageRepository/chargeOrderStorageRepository";
export const RecapViewNavigationName = "recap";

@Router.registerRoute({
  name: Router.Self,
  route: RecapViewNavigationName,
})
export default class RecapViewModel extends ScreenBase {
  public paymentViewModel: PaymentViewModel;
  public termsAgreementViewModel: TermsAgreementViewModel;
  public turnstileViewModel: TurnstileViewModel;
  public orderRecapitulationViewModel: OrderRecapitulationViewModel;
  public notificationPreferencesRecapViewModel: NotificationPreferencesRecapViewModel;
  public gdprInfoViewModel: GdprInfoViewModel;
  private unsubscriptions: Array<() => void> = [];

  @observable errorMessage?: ErrorMessage;
  @observable chargeOrderStatus?: ChargeOrderStatus;
  @observable formElement?: HTMLFormElement;
  @observable googlePayMetadata: GooglePayMetadata | undefined;
  @observable googlePayPaymentFingerprint?: string;
  @observable googlePayFingerprintProcessed = false;
  @observable isRequestBlocked = false;

  constructor(
    public chargeOrder: ChargeOrder,
    public payOrder: (
      errorMessage: ErrorMessage,
      paymentData?: google.payments.api.PaymentData,
      token?: string | null
    ) => Promise<void>,
    public chargeOrderService: ChargeOrderService,
    public repository: ChargeOrderRepository,
    public localizationService: LocalizationService,
    private paymentService: PaymentService,
    public router: AppRouter,
    private eventBus: EventBus,
    public storage: ChargeOrderStorageRepository,
    private paymentViewModelFactory: ReturnType<typeof PaymentViewModel.Factory>,
    private turnstileViewModelFactory: ReturnType<typeof TurnstileViewModel.Factory>,
    private termsAgreementViewModelFactory: ReturnType<typeof TermsAgreementViewModel.Factory>,
    private orderRecapitulationViewModelFactory: ReturnType<typeof OrderRecapitulationViewModel.Factory>,
    private notificationPreferencesRecapViewModelFactory: ReturnType<typeof NotificationPreferencesRecapViewModel.Factory>,
    private gdprInfoViewModelFactory: ReturnType<typeof GdprInfoViewModel.Factory>
  ) {
    super();
    attachAutomaticBlurValidator(this.chargeOrder, ChargeOrderValidationRules);
    this.navigationName = RecapViewNavigationName;
    this.nameValue = "eshop.title";
    this.paymentViewModel = this.paymentViewModelFactory(this.chargeOrder);
    this.turnstileViewModel = this.turnstileViewModelFactory("singleEshop", false);
    this.orderRecapitulationViewModel = this.orderRecapitulationViewModelFactory();
    this.termsAgreementViewModel = this.termsAgreementViewModelFactory(this.chargeOrder);
    this.notificationPreferencesRecapViewModel = this.notificationPreferencesRecapViewModelFactory(this.chargeOrder);
    this.gdprInfoViewModel = this.gdprInfoViewModelFactory();

    this.unsubscriptions.push(
      reaction(
        () => this.chargeOrder.paymentType,
        async paymentType => {
          await this.getGooglePayMetadata(paymentType);
        }
      ),
      this.eventBus.subscribe(getGooglePayMetadataSuccess, this.receivedGooglePayMetadata),
      this.eventBus.subscribe(getGooglePayMetadataFailure, this.googlePayMetadataFetchFailure),
      this.eventBus.subscribe(submitGooglePayPaymentFailure, this.googlePayPaymentFailure),
      this.eventBus.subscribe(submitGooglePayPaymentBrowserFingerprint, this.googlePayPaymentBrowserFingerprint),
      this.eventBus.subscribe(paymentEmailAddressInvalid, this.onPaymentEmailAddressInvalid)
    );
    this.getGooglePayMetadata(this.chargeOrder.paymentType);
  }

  protected onInitialize(): Promise<any> | void {
    this.unsubscriptions.push(
      reaction(
        () => [this.chargeOrder._termsAgreement, this.paymentViewModel.chargeOrder.paymentType],
        this.updateChargeOrderItem
      )
    );

    return super.onInitialize();
  }

  protected async onActivate() {
    if (this.stepper) {
      scrollToElement(this.stepper);
    }
    this.errorMessage = undefined;
    this.isRequestBlocked = false;
    this.checkRecapStoreData();

    if (!this.chargeOrder.paymentType) {
      this.chargeOrder.paymentType = PaymentType.PayGate;
    }

    if (this.chargeOrder.paymentType === PaymentType.Transfer && this.transferDisabled) {
      this.chargeOrder.paymentType = PaymentType.PayGate;
    }

    await this.notificationPreferencesRecapViewModel.activate();

    return super.onActivate();
  }

  protected onDeactivate(isClosing: boolean) {
    this.notificationPreferencesRecapViewModel.deactivate(isClosing);
    if (isClosing) {
      this.unsubscriptions.forEach(unsubscribe => unsubscribe());
      this.unsubscriptions = [];
    }
    return super.onDeactivate(isClosing);
  }

  @action.bound
  onOrderIsCompleteCheck(orderId: string) {
    this.eventBus.publish(paymentCompleteAlert({ orderId }));
  }

  @computed
  get isOrderComplete() {
    return this.chargeOrderStatus === ChargeOrderStatus.Paid || this.chargeOrderStatus === ChargeOrderStatus.PaidBankTransfer;
  }

  @computed
  get transferDisabled() {
    if (this.earliestPossibleDate) {
      return this.chargeOrder.charges.reduce(
        (summary, item) =>
          summary ||
          (item.validity.validSinceDate !== undefined &&
            isAfter(
              startOfCzechDay(new Date(this.earliestPossibleDate)),
              startOfCzechDay(new Date(item.validity.validSinceDate))
            )),
        false
      );
    }
    return false;
  }

  @computed
  get earliestPossibleDate() {
    return (
      this.chargeOrderService.chargeOrder._earliestPossibleValidSinceDate ??
      this.chargeOrderService.earliestPossibleValidSinceDate
    );
  }

  @computed
  get isDisabled() {
    return !!this.chargeOrder.paymentType && !!this.chargeOrder._termsAgreement;
  }

  @computed
  get isChecked() {
    return this.chargeOrder.paymentType && this.chargeOrder._termsAgreement;
  }

  @computed
  get isTransferPayment() {
    return this.chargeOrder.paymentType === PaymentType.Transfer;
  }

  @computed
  get buttonTextKey() {
    return this.isTransferPayment ? "payment" : "pay";
  }

  @computed
  get locale() {
    return this.localizationService.locale;
  }

  @computed
  get isGooglePayMetadataReady() {
    return !this.paymentService.isFetchingGooglePayMetadata && this.googlePayMetadata !== undefined;
  }

  @computed
  get isGooglePayPaymentMethodSelected() {
    return this.chargeOrder.paymentType === PaymentType.GooglePay;
  }

  @computed
  get stepper() {
    return window.document.getElementById("stepper");
  }

  @computed
  get isSmartRedirect() {
    return true;
  }

  @computed
  get isSingleChargeType() {
    return this.chargeOrderService.chargeOrderItem._singleChargeType;
  }

  @computed
  get shouldShowNotificationPreferences() {
    return !this.isSingleChargeType;
  }

  @action.bound
  translate(code: string) {
    return this.localizationService.translateGeneral(`eshop.order.summary.${code}`);
  }

  @action.bound
  checkRecapStoreData() {
    const { paymentType, _termsAgreement } = this.chargeOrderService.chargeOrder;

    this.chargeOrder.paymentType = paymentType;
    this.chargeOrder._termsAgreement = _termsAgreement;

    this.chargeOrderService.persistStoredChargeOrder(this.chargeOrderService.chargeOrder);
  }

  @action.bound
  updateChargeOrderItem() {
    const chargeOrder = this.chargeOrderService.chargeOrder;
    const { _termsAgreement, paymentType } = this.chargeOrder;

    chargeOrder.paymentType = paymentType;
    chargeOrder._termsAgreement = _termsAgreement;

    if (this.isChecked && this.turnstileViewModel.turnstileToken) {
      this.turnstileViewModel.reset();
    }

    this.chargeOrderService.persistStoredChargeOrder(chargeOrder);
  }

  @action.bound
  protected async getGooglePayMetadata(paymentType: PaymentType | undefined) {
    if (paymentType === PaymentType.GooglePay) {
      await this.paymentService.getGooglePayMetadata();
    }
  }

  @action.bound
  async getOrderStatus(id: string) {
    const order = await this.repository.get(id);
    this.chargeOrderStatus = order.status;
  }

  @action.bound
  private resetTermsAgreement() {
    this.chargeOrder._termsAgreement = false;
  }

  @action.bound
  protected receivedGooglePayMetadata(data: { type: string; payload: { googlePayMetadata: GooglePayMetadata } }) {
    this.googlePayMetadata = data.payload.googlePayMetadata;
  }

  @action.bound
  protected googlePayMetadataFetchFailure(data: { type: string; payload: void }) {
    this.googlePayMetadata = undefined;
  }

  @action.bound
  protected googlePayPaymentBrowserFingerprint(googlePayPaymentBrowserFingerprint: { payload: { url: string } }) {
    this.googlePayPaymentFingerprint = googlePayPaymentBrowserFingerprint.payload.url;
  }

  @action.bound
  protected googlePayPaymentFailure() {
    this.googlePayPaymentFingerprint = undefined;
    this.googlePayFingerprintProcessed = false;
  }

  @action.bound
  onGooglePayPaymentFingerprintLoaded = () => {
    if (!this.googlePayFingerprintProcessed) {
      this.googlePayFingerprintProcessed = true;
      this.eventBus.publish(googlePayPaymentBrowserFingerprintLoaded());
    }
  };

  @action.bound
  validateOrder() {
    this.chargeOrderService.persistChargeOrder();
    if (!validate(this.chargeOrder)) {
      this.errorMessage = "invalidData";
      return false;
    }

    return this.chargeOrderService.validateOrder();
  }

  @action.bound
  async payment() {
    let errorMessage: ErrorMessage = undefined;
    if (!this.chargeOrderService.validateOrder()) {
      errorMessage = "invalidData";
      await this.payOrder(errorMessage);
      return;
    }

    await this.payOrder(undefined, errorMessage, this.isTransferPayment ? this.turnstileViewModel.turnstileToken : undefined);
  }

  @action.bound
  public onContinue() {
    if (!this.validateAll()) {
      this.payOrder(this.errorMessage);
      return;
    }
    this.payment();
  }

  @action.bound
  public onBack() {
    this.isRequestBlocked = false;
    this.errorMessage = undefined;
    this.chargeOrderService.persistStoredChargeOrder(this.chargeOrder);
    this.router.navigate(ContactViewModel);
  }

  @action.bound
  private validateAll() {
    this.errorMessage = undefined;
    const isChargeOrderValid = validate(this.chargeOrder);
    const isNotificationPreferencesValid = validate(this.chargeOrder.notificationPreferences);
    const isValid = isChargeOrderValid && isNotificationPreferencesValid;

    if (!isValid) {
      this.errorMessage = "invalidData";
    }

    return isValid;
  }

  @action.bound
  onPaymentEmailAddressInvalid() {
    this.isRequestBlocked = true;
  }

  static Factory({ container }: interfaces.Context) {
    return (
      chargeOrder: ChargeOrder,
      payOrder: (
        errorMessage: ErrorMessage,
        paymentData?: google.payments.api.PaymentData,
        token?: string | null
      ) => Promise<void>
    ) =>
      new RecapViewModel(
        chargeOrder,
        payOrder,
        container.get(ChargeOrderService),
        container.get(ChargeOrderRepository),
        container.get(LocalizationService),
        container.get(PaymentService),
        container.get(AppRouter),
        container.get(EventBus),
        container.get(ChargeOrderStorageRepository),
        container.get(PaymentViewModel.Factory),
        container.get(TurnstileViewModel.Factory),
        container.get(TermsAgreementViewModel.Factory),
        container.get(OrderRecapitulationViewModel.Factory),
        container.get(NotificationPreferencesRecapViewModel.Factory),
        container.get(GdprInfoViewModel.Factory)
      );
  }
}
